import React from 'react';
import {
  BookMinus, BookPlus, Layers, ChevronUp, ChevronDown,
} from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import { Box, Text } from '@gluestack-ui/themed';
import { useFormContext } from 'react-hook-form';
import { useApiErrorReporter } from '@/common/utils/apiErrorReporter';
import type { CourseModule, CourseSegment } from '@/common/api/models';
import type { ColumnDefinition } from '@/admin/common/DataColumnConfig';
import useLinkedData from '@/admin/hooks/useLinkedData';
import { MembershipControl } from '@/admin/common/MembershipControl';
import ModuleSettingsEditor from './ModuleSettingsEditor';
import BulkSetSegmentModal from './BulkSetSegmentModal';

interface CourseModulesFormProps {
  route: {
    params: {
      data?: {
        id: string;
      };
    };
  };
}

/**
 * Enriched CourseModule with UI-specific display properties for the table.
 *
 * Pattern: Derived type built on Orval-generated CourseModule.
 * These display fields are computed from form state and API data for table rendering.
 */
interface CourseModuleTableRow extends Omit<CourseModule, 'courseSettings'> {
  courseSettings?: Record<string, unknown>; // Flexible settings for legacy/custom fields
  displaySegmentTitle: string;
  displaySegmentId?: string;
  displayTitle: string;
  displayAuthor: string;
}

/**
 * Raw module data from form state (may have incomplete shape)
 */
interface FormModuleData {
  id?: string;
  moduleId?: string;
  title?: string;
  authorName?: string;
  author?: {
    name?: string;
  };
}

/**
 * Column definitions for module table display.
 * Extracted to enable proper type inference (row is CourseModuleTableRow).
 *
 * Note: These columns are created dynamically in the component because they need
 * access to `segments` state for computing sort index. This is an acceptable
 * exception to the extraction pattern.
 */
const createModuleColumns = (
  segments: CourseSegment[],
): Record<string, ColumnDefinition<CourseModuleTableRow>> => ({
  segmentId: {
    label: 'Segment',
    type: 'string',
    width: 150,
    sortable: false,
    render: (row) => <Text size="xs" isTruncated>{row.displaySegmentTitle || '-'}</Text>,
  },
  'courseSettings.sortIndex': {
    label: '#',
    type: 'string',
    width: 50,
    sortable: false,
    render: (row) => {
      const moduleId = row.id;
      const segment = segments.find((s) => s.moduleIds?.includes(moduleId));
      if (!segment || !segment.moduleIds) return <Text size="xs">0</Text>;
      const index = segment.moduleIds.indexOf(moduleId);
      return <Text size="xs">{index !== -1 ? index + 1 : '0'}</Text>;
    },
  },
  title: {
    label: 'Module Title',
    type: 'string',
    width: 300,
    sortable: true,
    render: (row) => <Text size="xs">{row.displayTitle}</Text>,
  },
  'author.name': {
    label: 'Author',
    type: 'string',
    width: 150,
    sortable: true,
    render: (row) => <Text size="xs" isTruncated>{row.displayAuthor}</Text>,
  },
});

/**
 * Column definitions for add module modal
 */
const moduleAddColumns: Record<string, ColumnDefinition<CourseModule>> = {
  title: {
    label: 'Module Title',
    type: 'string',
    width: 300,
    sortable: true,
  },
  'author.name': {
    label: 'Author',
    type: 'string',
    width: 150,
    sortable: true,
  },
  'state.workflowState': {
    label: 'State',
    type: 'string',
    width: 100,
    sortable: true,
  },
};

const CourseModulesForm: React.FC<CourseModulesFormProps> = ({ route }) => {
  const { t } = useTranslation();
  const { reportApiError } = useApiErrorReporter();
  const { watch, setValue } = useFormContext();

  const segmentsWatch = watch('segments') as CourseSegment[] | undefined;
  const modulesWatch = watch('modules');

  const segments = React.useMemo(() => segmentsWatch || [], [segmentsWatch]);
  const formModules = React.useMemo(() => {
    const raw = modulesWatch;
    // Extract the array if it's nested in a 'data' property (common for API responses)
    const array = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' && 'data' in raw) ? (raw as { data: unknown }).data : [];
    return (Array.isArray(array) ? array : []) as FormModuleData[];
  }, [modulesWatch]);

  const courseId = route.params.data?.id;

  // Fetch full CourseModule objects (with titles/authors) from the dedicated modules sub-resource
  const { data: richModules, updateLinkMutation } = useLinkedData({
    parentObjectName: 'courses',
    parentId: courseId || '',
    objectName: 'modules',
    currentPage: 1,
    currentPageSize: 1000,
  });

  const [bulkModalVisible, setBulkModalVisible] = React.useState(false);
  const [selectedModules, setSelectedModules] = React.useState<CourseModule[]>([]);
  const [bulkActionModules, setBulkActionModules] = React.useState<CourseModule[]>([]);
  const [pendingAddModuleId, setPendingAddModuleId] = React.useState<string | null>(null);
  const [isBulkActionRunning, setIsBulkActionRunning] = React.useState(false);

  /**
   * Curriculum-ordered table data.
   * Merges "Rich" data (titles/authors) from the API with "Order" data (segments) from the form state.
   */
  const tableData = React.useMemo((): CourseModuleTableRow[] => {
    // Map rich modules for O(1) lookup by ID
    if (!Array.isArray(richModules)) return [];
    const richMap = new Map<string, CourseModule>(richModules.map((m) => [m.id, m]));

    const orderedList: CourseModuleTableRow[] = [];

    // Add modules in the order defined by the segments
    const includedModuleIds = new Set<string>();

    segments.forEach((segment) => {
      (segment.moduleIds || []).forEach((mId) => {
        includedModuleIds.add(String(mId));
        // Prefer the rich data from the API to get titles/authors
        const richMod = richMap.get(mId);
        const formMod = formModules.find((m) => String(m.id || m.moduleId) === String(mId));

        if (richMod || formMod) {
          const displayTitle = richMod?.title || formMod?.title || '-';
          const displayAuthor = richMod?.author?.name || formMod?.authorName || formMod?.author?.name || '-';

          // Use rich module as base, fallback to a minimal CourseModule shape
          const baseModule: CourseModule = richMod || {
            id: mId,
            title: displayTitle,
            metadata: { ects: 0, learningHours: 0 },
            state: { workflowState: 'draft' },
          };

          orderedList.push({
            ...baseModule,
            // Ensure courseSettings has the current segmentId from the form's perspective.
            // This is critical for the EditMemberModal to initialize with the correct segment,
            // even if the module was just moved within the parent form.
            courseSettings: {
              ...(baseModule.courseSettings || {}),
              segmentId: segment.id,
            } as Record<string, unknown>,
            id: mId, // Normalize ID for table selection
            displaySegmentTitle: segment.title,
            displaySegmentId: segment.id,
            displayTitle,
            displayAuthor,
          });
        }
      });
    });

    // Add legacy modules that are not assigned to any segment
    formModules.forEach((formMod) => {
      const mId = String(formMod.id || formMod.moduleId);
      if (mId && !includedModuleIds.has(mId)) {
        const richMod = richMap.get(mId);
        const displayTitle = richMod?.title || formMod.title || '-';
        const displayAuthor = richMod?.author?.name || formMod.authorName || formMod.author?.name || '-';

        const baseModule: CourseModule = richMod || {
          id: mId,
          title: displayTitle,
          metadata: { ects: 0, learningHours: 0 },
          state: { workflowState: 'draft' },
        };

        orderedList.push({
          ...baseModule,
          courseSettings: baseModule.courseSettings as Record<string, unknown>,
          id: mId,
          displaySegmentTitle: '', // Empty for legacy
          displaySegmentId: undefined,
          displayTitle,
          displayAuthor,
        });
      }
    });

    return orderedList;
  }, [segments, richModules, formModules]);

  // We still need useLinkedData for the mutation actions (link/unlink)
  // Handle errors for membership operations (add, remove, update)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleMembershipError = (error: any) => {
    reportApiError(error, t('admin.course.modules.errors.membershipAction', { defaultValue: 'Membership Operation' }));
  };

  const moveModule = (id: string, direction: 'up' | 'down') => {
    const newSegments = [...segments];
    const segmentIndex = newSegments.findIndex((s) => s.moduleIds?.includes(id));
    if (segmentIndex === -1) return; // eslint-disable-line no-useless-return

    const segment = { ...newSegments[segmentIndex] };
    if (!segment.moduleIds) return; // eslint-disable-line no-useless-return

    const moduleIds = [...segment.moduleIds];
    const idx = moduleIds.indexOf(id);
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;

    if (newIdx >= 0 && newIdx < moduleIds.length) {
      [moduleIds[idx], moduleIds[newIdx]] = [moduleIds[newIdx], moduleIds[idx]];
      segment.moduleIds = moduleIds;
      newSegments[segmentIndex] = segment;
      setValue('segments', newSegments, { shouldDirty: true });
    }
  };

  const handleSetSegment = async (segmentId: string) => {
    const itemsToUpdate = bulkActionModules.length > 0
      ? bulkActionModules
      : (pendingAddModuleId ? [{ id: pendingAddModuleId } as CourseModule] : selectedModules);

    const selectedIds = itemsToUpdate.map((m) => m.id);

    const newSegments = segments.map((s) => {
      // 1. Remove modules from all segments first (to move them)
      //    We must convert both ID types to strings for robust comparison
      const moduleIds = (s.moduleIds || []).filter((id) => !selectedIds.map(String).includes(String(id)));

      // 2. Add them to the target segment
      if (String(s.id) === String(segmentId)) {
        return { ...s, moduleIds: [...moduleIds, ...selectedIds] };
      }
      return { ...s, moduleIds };
    });

    setValue('segments', newSegments, { shouldDirty: true });

    setIsBulkActionRunning(true);
    try {
      // Also update individual module properties for backend compatibility until full curriculum save is wired
      await Promise.all(selectedIds.map((id) => updateLinkMutation.mutateAsync({
        id,
        data: {
          segmentId,
          courseSettings: {
            segmentId,
          },
        },
      })));
    } catch (error) {
      reportApiError(error, t('admin.course.modules.errors.updateSegment', { defaultValue: 'Update Segment' }));
    } finally {
      setIsBulkActionRunning(false);
      setPendingAddModuleId(null);
      setBulkActionModules([]);
      setBulkModalVisible(false);
    }
  };

  const handleLinkSuccess = (moduleId: string | number) => {
    // 1. Add to the modules master list in form state so tableData can find it
    const idStr = String(moduleId);
    const currentModules = watch('modules') || [];
    const modulesList = Array.isArray(currentModules) ? currentModules as FormModuleData[] : [];

    if (!modulesList.some((m) => String(m.id || m.moduleId) === idStr)) {
      // Add a stub module; the rich query will eventually fill in the details
      setValue('modules', [...modulesList, { id: idStr }], { shouldDirty: true });
    }

    // 2. Ask which segment it belongs to
    if (segments.length > 0) {
      setPendingAddModuleId(idStr);
      setBulkModalVisible(true);
    }
  };

  const handleUnlinkSuccess = (moduleId: string | number) => {
    const idStr = String(moduleId);

    // 1. Remove from segments
    const newSegments = segments.map((s) => ({
      ...s,
      moduleIds: (s.moduleIds || []).filter((id) => String(id) !== idStr),
    }));
    setValue('segments', newSegments, { shouldDirty: true });

    // 2. Remove from modules master list in form state
    const currentModules = watch('modules') || [];
    const modulesList = Array.isArray(currentModules) ? currentModules as FormModuleData[] : [];
    const newModules = modulesList.filter((m) => String(m.id || m.moduleId) !== idStr);
    setValue('modules', newModules, { shouldDirty: true });
  };

  /**
   * Handler for module edit completion.
   *
   * Pattern: Reads the segment assignment from the saved form data and updates
   * the curriculum order structure. This ensures the table reflects the new
   * segment assignment after the modal is closed.
   *
   * @param variables - The save data from EditMemberModal
   */
  const handleEditMemberSuccess = async (variables: {
    id: string | number;
    data: Record<string, unknown>;
  }) => {
    const moduleId = String(variables.id);
    const newSegmentId = (variables.data.courseSettings as { segmentId?: string })?.segmentId;

    // Update API
    await updateLinkMutation.mutateAsync({
      id: variables.id,
      data: variables.data,
    });

    // If segment changed, update local curriculum structure
    if (newSegmentId) {
      const newSegments = segments.map((s) => {
        // Remove module from all segments first
        const moduleIds = (s.moduleIds || []).filter((id) => String(id) !== moduleId);

        // Add to the new segment
        if (String(s.id) === String(newSegmentId)) {
          return { ...s, moduleIds: [...moduleIds, moduleId] };
        }
        return { ...s, moduleIds };
      });

      setValue('segments', newSegments, { shouldDirty: true });
    }
  };

  /**
   * Edit modal configuration for module settings.
   *
   * Pattern: ModuleSettingsEditor receives read-only segments for dropdown choices.
   * It stores segment changes in the form field 'courseSettings.segmentId'.
   * The parent reads these changes when the modal is saved and updates the curriculum order.
   */
  const editModalColumns = React.useMemo(() => ({
    settings_group: {
      label: '', // Hide label
      type: 'custom' as const,
      component: ModuleSettingsEditor,
      componentProps: {
        segments, // Read-only prop for dropdown choices
      },
    },
  }), [segments]);

  // Create columns with current segments context
  const moduleColumns = React.useMemo(() => createModuleColumns(segments), [segments]);

  return (
    <Box flex={1} bg="$backgroundLight0">
      <MembershipControl<'modules', CourseModuleTableRow>
        label="Modules"
        noTitle
        noPadding
        parentId={courseId || ''}
        parentObjectName="courses"
        objectName="modules"
        excludeCurrent
        overrideData={tableData} // Use curriculum-ordered local state
        isActionRunning={isBulkActionRunning}
        defaultSort="" // Use provided order, disable auto-sorting
        doubleConfirmUnlink
        unlinkConfirmationTitle={t('errors.membership.remove_module_title', { defaultValue: 'Remove Module from Course' })}
        unlinkConfirmationMessage={t('errors.membership.remove_module_message', { defaultValue: 'Removing a module from this course will result in the loss of all student work, submissions and grades associated with this module in this specific course. This action cannot be undone.' })}
        onEditMemberSuccess={handleEditMemberSuccess}
        columns={moduleColumns}
        editModalColumns={editModalColumns}
        icons={{
          add: BookPlus,
          remove: BookMinus,
        }}
        onLinkError={handleMembershipError}
        onSelectionChange={(selected) => setSelectedModules(selected as CourseModule[])}
        onLinkSuccess={handleLinkSuccess}
        onUnlinkSuccess={handleUnlinkSuccess}
        addMemberFilter={{ state: 'published' }}
        addModalColumns={moduleAddColumns}
        heightsOverrides={{ header: 260 }}
        pageSize={10}
        extraActions={{
          moveUp: {
            label: 'Move Up',
            icon: ChevronUp,
            position: 'row',
            onAction: (row: CourseModuleTableRow) => moveModule(row.id, 'up'),
            availCondition: (row: CourseModuleTableRow) => {
              const segment = segments?.find((s) => s.moduleIds?.includes(row.id));
              return (segment?.moduleIds?.indexOf(row.id) ?? 0) > 0;
            },
          },
          moveDown: {
            label: 'Move Down',
            icon: ChevronDown,
            position: 'row',
            onAction: (row: CourseModuleTableRow) => moveModule(row.id, 'down'),
            availCondition: (row: CourseModuleTableRow) => {
              const segment = segments?.find((s) => s.moduleIds?.includes(row.id));
              const idx = segment?.moduleIds?.indexOf(row.id) ?? -1;
              return idx !== -1 && idx < (segment?.moduleIds?.length ?? 0) - 1;
            },
          },
          bulkSetSegment: {
            label: 'Set Segment',
            icon: Layers,
            bulk: true,
            // position is not needed for bulk actions, they only show in overlay
            onAction: (items: CourseModule[]) => {
              setBulkActionModules(items);
              setBulkModalVisible(true);
            },
          },
          bulkDelete: {
            label: t('errors.membership.bulk_remove_modules_title', { defaultValue: 'Remove Selected Modules' }),
            icon: BookMinus,
            bulk: true,
          },
        }}
      />

      <BulkSetSegmentModal
        visible={bulkModalVisible}
        onClose={() => {
          setBulkModalVisible(false);
          setPendingAddModuleId(null);
        }}
        onConfirm={handleSetSegment}
        courseId={courseId || ''}
        title={pendingAddModuleId ? 'Select Segment for Module' : 'Set Segment for Selection'}
        description={pendingAddModuleId ? 'Assign this module to a specific segment:' : 'Assign selected modules to a specific segment:'}
      />
    </Box>
  );
};

export default CourseModulesForm;
